import { useEffect, useMemo, useRef, useState } from 'react';
import { codeLabel } from '../../utils/codeLabel';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type {
  AdminUserResponse,
  PaymentDirection,
  PaymentQueueBucket,
  PaymentQueueRow,
  PaymentSlaState,
  PaymentTransactionFilters,
} from '../../api/types';
import { CounterpartyPicker } from './CounterpartyPicker';
import { OrderLabel } from './OrderLabel';
import { StatusChip } from './StatusChip';
import { OrderQueueTable } from './OrderQueueTable';
import { GenerateInvoiceDialog } from '../invoices/GenerateInvoiceDialog';
import { mapPaymentError, PAYMENT_MESSAGES } from '../../api/endpoints/payments';
import { useOpsUser } from '../ownership/opsUser';
import {
  useApproveCharge,
  useApprovePayment,
  useRefreshPayments,
  useMarkPaymentPaid,
  useOpenPaymentItems,
  usePayment,
  usePaymentQueue,
  usePaymentTransactions,
  useRecordReceipt,
  useRejectCharge,
  useRejectPayment,
  useRequestPayout,
  useVoidPayment,
  useVoidPaymentAllocation,
} from './hooks';
import { formatMoney, parsePaise } from './allocation';
import { MarkPaidDialog } from './MarkPaidDialog';
import { NewPayoutDialog } from './NewPayoutDialog';
import { AllocateReceiptCreditFlow } from './AllocateReceiptCreditFlow';
import { PaymentDrawer } from './PaymentDrawer';
import { addCalendarDays, istDateInput } from './paymentDates';
import styles from './Payments.module.css';
import { CancelPaymentDialog } from './CancelPaymentDialog';
import { VoidAllocationDialog } from './VoidAllocationDialog';
import { ReasonDialog } from './ReasonDialog';
import { ReviewPartnerChargeFlow } from './ReviewPartnerChargeFlow';
import { VerifyClaimFlow } from './VerifyClaimFlow';
import { RecordReceiptDialog } from './RecordReceiptDialog';
import { SlaChip } from './SlaChip';
import { TransactionsTab } from './TransactionsTab';
import { Button } from '../../components/ui/Button';
import { isDemo } from '../../demo/flag';
import { PaidNotBilledTab } from './PaidNotBilledTab';
import { MyApprovalsTab } from './MyApprovalsTab';

const LABELS: Record<PaymentQueueBucket, string> = {
  PAYMENT_CLAIMS: 'Payment claims to verify',
  ADVANCE_DUE: 'Advance due',
  APPROVALS: 'Awaiting approval',
  TO_PAY: 'Approved: to pay',
  BALANCE_DUE: 'Balance due',
  TO_INVOICE: 'To invoice / bill',
  COLLECTIONS: 'Shipper overdue',
  UNALLOCATED_RECEIPTS: 'Unallocated receipts',
  PAYEE_NOT_READY: 'Payee not ready',
  CANCELLED_WITH_MONEY: 'Money on cancelled orders',
};
const BUCKETS = Object.keys(LABELS) as PaymentQueueBucket[];
const SLA_STATES: PaymentSlaState[] = ['ON_TRACK', 'AT_RISK', 'BREACHED'];
const SLA_ORDER: Record<PaymentSlaState, number> = { BREACHED: 0, AT_RISK: 1, ON_TRACK: 2 };
type ReasonAction =
  | { kind: 'reject'; paymentId: number }
  | { kind: 'rejectCharge'; chargeId: number }
  | { kind: 'voidPayment'; paymentId: number }
  | { kind: 'voidAllocation'; paymentId: number; allocationId: number };
type ActionError = { key: string; message: string };

function detailError(error: unknown) {
  const candidate = error as {
    code?: keyof typeof PAYMENT_MESSAGES;
    response?: { data?: { code?: keyof typeof PAYMENT_MESSAGES } };
  } | null;
  const code = candidate?.code ?? candidate?.response?.data?.code;
  return code && Object.prototype.hasOwnProperty.call(PAYMENT_MESSAGES, code)
    ? PAYMENT_MESSAGES[code]
    : 'Could not load payment details. Try again.';
}

function isPaymentBucket(bucket?: PaymentQueueBucket) {
  return bucket === 'APPROVALS' || bucket === 'TO_PAY' || bucket === 'PAYMENT_CLAIMS';
}
function sortedRows(rows: PaymentQueueRow[]) {
  return [...rows].sort(
    (left, right) =>
      SLA_ORDER[left.slaState] - SLA_ORDER[right.slaState] ||
      (left.dueAt ?? '9999').localeCompare(right.dueAt ?? '9999') ||
      (left.shipmentId ?? 0) - (right.shipmentId ?? 0),
  );
}

export function PaymentsView() {
  const { t } = useTranslation();
  const { isManager } = useOpsUser();
  const refreshPayments = useRefreshPayments();
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedBucket = searchParams.get('bucket');
  const linkedState = searchParams.get('state');
  const requestedBucket = BUCKETS.includes(linkedBucket as PaymentQueueBucket)
    ? (linkedBucket as PaymentQueueBucket)
    : null;
  const stateFilter = SLA_STATES.includes(linkedState as PaymentSlaState)
    ? (linkedState as PaymentSlaState)
    : null;
  const landingFilter = useRef<string | null>(null);
  const [mineChoice, setMineChoice] = useState<boolean | null>(null);
  const linkedMine = searchParams.get('mine');
  const mine =
    mineChoice ??
    (linkedMine === 'true' ? true : linkedMine === 'false' && isManager ? false : !isManager);
  const [bucket, setBucket] = useState<PaymentQueueBucket>(requestedBucket ?? 'ADVANCE_DUE');
  const [selection, setSelection] = useState<PaymentQueueRow[]>([]);
  const [payoutPurpose, setPayoutPurpose] = useState<'ADVANCE' | 'BALANCE' | undefined>();
  const [payoutBlockReason, setPayoutBlockReason] = useState<string | null>();
  const [prefillIds, setPrefillIds] = useState<number[]>([]);
  const [invoiceId, setInvoiceId] = useState<number | null>(null);
  const [transactions, setTransactions] = useState(false);
  // Demo world only until the build issue wires rhino-api (#293).
  const [paidNotBilled, setPaidNotBilled] = useState(false);
  const [myApprovals, setMyApprovals] = useState(false);
  const today = istDateInput();
  const [transactionFilters, setTransactionFilters] = useState<PaymentTransactionFilters>({
    from: addCalendarDays(today, -29),
    to: today,
    page: 0,
  });
  const [creditSuccess, setCreditSuccess] = useState<string | null>(null);
  const [creditPaymentId, setCreditPaymentId] = useState<number | null>(null);
  const startCredit = (id: number) => {
    setSelectedPaymentId(null);
    setCreditPaymentId(id);
  };
  const [reviewCharge, setReviewCharge] = useState<PaymentQueueRow | null>(null);
  const [claimId, setClaimId] = useState<number | null>(null);
  const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(null);
  const [markPaidId, setMarkPaidId] = useState<number | null>(null);
  const [reasonAction, setReasonAction] = useState<ReasonAction | null>(null);
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<ActionError | null>(null);
  const transactionQuery = usePaymentTransactions(transactionFilters);
  const paymentQuery = usePayment(selectedPaymentId ?? undefined);
  const markPaidQuery = usePayment(markPaidId ?? undefined);
  const markPaidPayee = useOpenPaymentItems(
    'OUT',
    markPaidId ? markPaidQuery.data?.counterpartyUserId : undefined,
  );
  const approvePayment = useApprovePayment();
  const rejectPayment = useRejectPayment();
  const approveCharge = useApproveCharge();
  const rejectCharge = useRejectCharge();
  const markPaymentPaid = useMarkPaymentPaid();
  const voidPayment = useVoidPayment();
  const voidAllocation = useVoidPaymentAllocation();
  const recordReceipt = useRecordReceipt();
  const requestPayout = useRequestPayout();
  const [entryDirection, setEntryDirection] = useState<PaymentDirection | null>(null);
  const [counterparty, setCounterparty] = useState<AdminUserResponse | null>(null);
  const openItems = useOpenPaymentItems(entryDirection ?? 'IN', counterparty?.id);
  const query = usePaymentQueue(mine);
  const buckets = query.data?.buckets ?? [];
  useEffect(() => {
    const key = `${stateFilter}:${mine}`;
    if (!stateFilter || requestedBucket || !query.data || landingFilter.current === key) return;
    landingFilter.current = key;
    const matching = query.data.buckets.find((entry) =>
      entry.rows.some((row) => row.slaState === stateFilter),
    );
    if (matching) setBucket(matching.bucket);
  }, [stateFilter, mine, requestedBucket, query.data]);
  const active = buckets.find((entry) => entry.bucket === bucket);
  useEffect(() => {
    if (requestedBucket) {
      setTransactions(false);
      setPaidNotBilled(false);
      setMyApprovals(false);
      setBucket(requestedBucket);
    }
  }, [requestedBucket]);
  useEffect(() => {
    setSelection([]);
  }, [mine, bucket, transactions]);
  const visibleRows = useMemo(
    () =>
      sortedRows(
        (active?.rows ?? []).filter((row) => !stateFilter || row.slaState === stateFilter),
      ),
    [active?.rows, stateFilter],
  );
  useEffect(() => {
    setSelection((old) => {
      const current = old.flatMap((selected) => {
        const row = visibleRows.find(
          (item) =>
            item.shipmentId === selected.shipmentId &&
            item.fleetManagerUserId === selected.fleetManagerUserId,
        );
        return row ? [row] : [];
      });
      return current.length === old.length && current.every((row, index) => row === old[index])
        ? old
        : current;
    });
  }, [visibleRows]);
  const submitReason = async (reason: string) => {
    if (!reasonAction || !isManager) return;
    setReasonError(null);
    try {
      if (reasonAction.kind === 'reject')
        await rejectPayment.mutateAsync({ paymentId: reasonAction.paymentId, reason });
      else if (reasonAction.kind === 'rejectCharge')
        await rejectCharge.mutateAsync({ chargeId: reasonAction.chargeId, reason });
      else if (reasonAction.kind === 'voidPayment')
        await voidPayment.mutateAsync({ paymentId: reasonAction.paymentId, reason });
      else await voidAllocation.mutateAsync({ allocationId: reasonAction.allocationId, reason });
      setReasonAction(null);
    } catch (error) {
      setReasonError(mapPaymentError(error));
    }
  };
  const openReason = (action: ReasonAction) => {
    setReasonError(null);
    setReasonAction(action);
  };
  const approve = async (key: string, action: () => Promise<unknown>) => {
    setActionError(null);
    try {
      await action();
    } catch (error) {
      setActionError({ key, message: mapPaymentError(error) });
    }
  };

  const startEntry = (
    direction: PaymentDirection,
    id?: number,
    name?: string | null,
    ids: number[] = [],
  ) => {
    setPayoutPurpose(undefined);
    setPayoutBlockReason(null);
    setEntryDirection(direction);
    setPrefillIds(ids);
    setCounterparty(id == null ? null : ({ id, fullName: name ?? null } as AdminUserResponse));
  };
  const startPayout = (rows: PaymentQueueRow[]) => {
    if (
      !rows.length ||
      rows[0].fleetManagerUserId == null ||
      rows.some((row) => row.fleetManagerUserId !== rows[0].fleetManagerUserId)
    )
      return;
    startEntry(
      'OUT',
      rows[0].fleetManagerUserId,
      rows[0].partnerName,
      rows.flatMap((row) => (row.shipmentId == null ? [] : [row.shipmentId])),
    );
    setPayoutPurpose(
      bucket === 'ADVANCE_DUE' ? 'ADVANCE' : bucket === 'BALANCE_DUE' ? 'BALANCE' : undefined,
    );
    setPayoutBlockReason(rows[0].payeeBlockReason);
  };
  const openMarkPaid = (id: number) => {
    setSelectedPaymentId(null);
    setMarkPaidId(id);
  };
  const selectTab = (value: PaymentQueueBucket) => {
    setTransactions(false);
    setPaidNotBilled(false);
    setMyApprovals(false);
    setBucket(value);
  };
  return (
    <div className={styles.body}>
      {stateFilter && (
        <p role="status">
          {t('payments.paymentsView.activeFilter')}{' '}
          {stateFilter === 'BREACHED' ? 'Breached' : 'At risk'} ·{' '}
          {t('payments.paymentsView.matchingRows', { rows: visibleRows.length, total: active?.totalCount ?? 0 })}
          <Button
            variant="secondary" size="sm"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('state');
              setSearchParams(next);
            }}
          >
            {t('payments.paymentsView.clearFilter')}
          </Button>
        </p>
      )}
      {creditSuccess && <p role="status">{creditSuccess}</p>}
      <div className={styles.card}>
        <div className={`${styles.sectionTitle} ${styles.queueHeader}`}>
          <div>
            <h3>{t('payments.paymentsView.heading')}</h3>
            <p>
              <Link to="/payments?state=BREACHED">
                <strong>
                  {query.data?.topStrip.breachedOrders ?? 0}{' '}
                  {query.data?.topStrip.breachedOrders === 1 ? 'order' : 'orders'}{' '}
                  {t('payments.paymentsView.breached')} ·{' '}
                  {query.data?.topStrip.breachedPayouts ?? 0}{' '}
                  {query.data?.topStrip.breachedPayouts === 1 ? 'payout' : 'payouts'}{' '}
                  {t('payments.paymentsView.breached')}
                  {(query.data?.topStrip.breachedClaims ?? 0) > 0 && (
                    <>
                      {' '}
                      · {query.data?.topStrip.breachedClaims}{' '}
                      {query.data?.topStrip.breachedClaims === 1 ? 'claim' : 'claims'}{' '}
                      {t('payments.paymentsView.breached')}
                    </>
                  )}
                </strong>
              </Link>{' '}
              ·{' '}
              <Link to="/payments?state=AT_RISK">
                {query.data?.topStrip.atRiskOrders ?? 0}{' '}
                {query.data?.topStrip.atRiskOrders === 1 ? 'order' : 'orders'}{' '}
                {t('payments.paymentsView.atRisk')} ·{' '}
                {query.data?.topStrip.atRiskPayouts ?? 0}{' '}
                {query.data?.topStrip.atRiskPayouts === 1 ? 'payout' : 'payouts'}{' '}
                {t('payments.paymentsView.atRisk')}
                {(query.data?.topStrip.atRiskClaims ?? 0) > 0 && (
                  <>
                    {' '}
                    · {query.data?.topStrip.atRiskClaims}{' '}
                    {query.data?.topStrip.atRiskClaims === 1 ? 'claim' : 'claims'}{' '}
                    {t('payments.paymentsView.atRisk')}
                  </>
                )}
              </Link>
            </p>
          </div>
          <div className={styles.actions}>
            <Button
              variant="primary" size="sm"
              onClick={() => {
                startEntry('IN');
              }}
            >
              {t('payments.paymentsView.recordReceipt')}
            </Button>
            <Button
              variant="primary" size="sm"
              onClick={() => {
                startEntry('OUT');
              }}
            >
              {t('payments.paymentsView.newPayout')}
            </Button>
            <Button
              aria-pressed={mine}
              variant={mine ? 'primary' : 'secondary'} size="sm"
              onClick={() => setMineChoice(true)}
            >
              {t('payments.paymentsView.mine')}
            </Button>
            <Button
              aria-pressed={!mine}
              variant={!mine ? 'primary' : 'secondary'} size="sm"
              onClick={() => setMineChoice(false)}
            >
              {t('payments.paymentsView.all')}
            </Button>
          </div>
        </div>
      </div>
      <div className={styles.kpis}>
        {(
          [
            ['Advance SLA', 'ADVANCE_DUE'],
            ['Approval SLA', 'APPROVALS'],
            ['Balance SLA', 'BALANCE_DUE'],
            ['To invoice/bill', 'TO_INVOICE'],
            ['Shipper overdue', 'COLLECTIONS'],
          ] as const
        ).map(([title, key]) => {
          const counts = buckets.find((entry) => entry.bucket === key);
          return (
            <button
              key={key}
              className={`${styles.card} ${styles.kpi}`}
              aria-label={title}
              onClick={() => selectTab(key)}
            >
              <span>{title}</span>
              <strong>{counts?.totalCount ?? 0}</strong>
              <div className={styles.kpiCounts}>
                <span className={counts?.breached ? styles.kpiBreached : styles.kpiMuted}>
                  {t('payments.paymentsView.kpiBreached', { n: counts?.breached ?? 0 })}
                </span>
                {' · '}
                <span className={counts?.atRisk ? styles.kpiAtRisk : styles.kpiMuted}>
                  {t('payments.paymentsView.kpiAtRisk', { n: counts?.atRisk ?? 0 })}
                </span>
                {' · '}
                <span className={styles.kpiMuted}>{t('payments.paymentsView.kpiOnTrack', { n: counts?.onTrack ?? 0 })}</span>
              </div>
            </button>
          );
        })}
      </div>
      <div className={styles.card}>
        <div role="tablist" className={styles.tabs}>
          {BUCKETS.map((key) => (
            <button
              role="tab"
              aria-selected={!transactions && !paidNotBilled && !myApprovals && bucket === key}
              key={key}
              className={styles.tab}
              onClick={() => selectTab(key)}
            >
              {LABELS[key]}{' '}
              <span className={styles.counter}>
                {buckets.find((entry) => entry.bucket === key)?.totalCount ?? 0}
              </span>
            </button>
          ))}
          <button
            role="tab"
            aria-selected={transactions}
            className={styles.tab}
            onClick={() => {
              setPaidNotBilled(false);
              setMyApprovals(false);
              setTransactions(true);
            }}
          >
            {t('payments.paymentsView.transactions')}
          </button>
          {isDemo() && (
            <button
              role="tab"
              aria-selected={paidNotBilled}
              className={styles.tab}
              onClick={() => {
                setTransactions(false);
                setMyApprovals(false);
                setPaidNotBilled(true);
              }}
            >
              {t('payments.paymentsView.paidNotBilled')}
            </button>
          )}
          {isDemo() && (
            <button
              role="tab"
              aria-selected={myApprovals}
              className={styles.tab}
              onClick={() => {
                setTransactions(false);
                setPaidNotBilled(false);
                setMyApprovals(true);
              }}
            >
              {t('payments.paymentsView.myApprovals')}
            </button>
          )}
        </div>
        {!transactions &&
          !paidNotBilled &&
          !myApprovals &&
          (bucket === 'ADVANCE_DUE' || bucket === 'BALANCE_DUE') &&
          selection.length > 0 && (
            <div className={styles.sectionTitle}>
              <span>{t('payments.paymentsView.ordersSelected', { n: selection.length })}</span>
              <Button variant="primary" size="sm" onClick={() => startPayout(selection)}>
                {t('payments.paymentsView.requestPayoutSelected')}
              </Button>
            </div>
          )}
        {myApprovals ? (
          <MyApprovalsTab />
        ) : paidNotBilled ? (
          <PaidNotBilledTab />
        ) : transactions ? (
          <TransactionsTab
            rows={transactionQuery.data?.content ?? []}
            totalElements={transactionQuery.data?.totalElements ?? 0}
            page={transactionQuery.data?.page ?? 0}
            onFiltersChange={setTransactionFilters}
            onOpenPayment={setSelectedPaymentId}
          />
        ) : query.isLoading ? (
          <p>{t('payments.paymentsView.loadingQueue')}</p>
        ) : query.isError ? (
          <button onClick={() => query.refetch()}>{t('payments.paymentsView.retryQueue')}</button>
        ) : !visibleRows.length ? (
          <p>{t('payments.paymentsView.emptyQueue')}</p>
        ) : isPaymentBucket(bucket) ? (
          <div className={styles.tableWrap}>
            <table className={`${styles.table} ${styles.queueTable}`}>
              <thead>
                <tr>
                  <th>{t('payments.paymentsView.colItem')}</th>
                  <th>{t('payments.paymentsView.colPartnerOrder')}</th>
                  <th>{t('payments.paymentsView.colOrdersType')}</th>
                  <th>{t('payments.paymentsView.colAmount')}</th>
                  <th>{t('payments.paymentsView.colSla')}</th>
                  <th>{t('payments.paymentsView.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => {
                  const chargeId = row.chargeId;
                  const paymentId = row.paymentId;
                  const charge = chargeId != null;
                  const key = charge ? `charge-${chargeId}` : `payment-${paymentId}`;
                  const pending = charge
                    ? approveCharge.isPending || rejectCharge.isPending
                    : approvePayment.isPending ||
                      rejectPayment.isPending ||
                      markPaymentPaid.isPending ||
                      voidPayment.isPending ||
                      voidAllocation.isPending;
                  return (
                    <tr key={key}>
                      <td>
                        {charge ? (
                          <>
                            <div>{t('payments.paymentsView.chargeLabel', { id: chargeId })}</div>
                            {row.requestedByUserId != null && (
                              <span>
                                {t('payments.paymentsView.partnerRequest')} ·{' '}
                                {row.counterpartyName ??
                                  row.partnerName ??
                                  `Partner #${row.requestedByUserId}`}
                              </span>
                            )}
                          </>
                        ) : (
                          <Button
                            variant="secondary" size="sm"
                            onClick={() => setSelectedPaymentId(paymentId)}
                          >
                            #{paymentId}
                          </Button>
                        )}
                      </td>
                      <td>
                        {charge ? (
                          row.shipmentId != null ? (
                            <OrderLabel
                              shipmentId={row.shipmentId}
                              route={row.route}
                              vehicleNumber={row.vehicleNumber}
                            />
                          ) : (
                            '—'
                          )
                        ) : (
                          <Button
                            variant="secondary" size="sm"
                            onClick={() => setSelectedPaymentId(paymentId)}
                          >
                            {row.counterpartyName ?? '—'}
                          </Button>
                        )}
                      </td>
                      <td>
                        {charge
                          ? (row.chargeType ? codeLabel('chargeType', row.chargeType) : '—')
                          : (row.orders?.map((order, index) => (
                              <div key={`${order.shipmentId}-${index}`}>
                                <OrderLabel {...order} newTab /> · {order.purpose.toLowerCase()} ·{' '}
                                {formatMoney(parsePaise(order.amount))}
                              </div>
                            )) ?? '—')}
                      </td>
                      <td>
                        {charge ? (
                          <>
                            {t('payments.paymentsView.shipperPartnerAmounts', {
                              shipper: formatMoney(parsePaise(row.shipperAmount ?? '0.00')),
                              partner: formatMoney(parsePaise(row.partnerAmount ?? '0.00')),
                            })}
                          </>
                        ) : (
                          formatMoney(parsePaise(row.amount))
                        )}
                      </td>
                      <td>
                        <StatusChip
                          displayStatus={
                            bucket === 'PAYMENT_CLAIMS'
                              ? 'CLAIMED'
                              : bucket === 'APPROVALS'
                                ? 'REQUESTED'
                                : 'APPROVED'
                          }
                        />{' '}
                        <SlaChip state={row.slaState} dueAt={row.dueAt} />
                      </td>
                      <td>
                        <div className={styles.queueActions}>
                          {chargeId != null && row.requestedByUserId != null && (
                            <Button variant="secondary" size="sm" onClick={() => setReviewCharge(row)}>
                              {t('payments.paymentsView.viewRequest')}
                            </Button>
                          )}
                          {active?.bucket === 'PAYMENT_CLAIMS' && paymentId != null && (
                            <Button
                              variant="primary" size="sm"
                              onClick={() => setClaimId(paymentId)}
                            >
                              {t('payments.paymentsView.verifyClaim')}
                            </Button>
                          )}
                          {active?.bucket === 'APPROVALS' && isManager && chargeId != null && (
                            <>
                              <Button
                                variant="primary" size="sm"
                                aria-label={t('payments.paymentsView.approveCharge')}
                                disabled={pending}
                                onClick={() =>
                                  row.requestedByUserId != null
                                    ? setReviewCharge(row)
                                    : approve(key, () => approveCharge.mutateAsync(chargeId))
                                }
                              >
                                {t('payments.paymentsView.approve')}
                              </Button>
                              <Button
                                variant="danger" size="sm"
                                aria-label={t('payments.paymentsView.rejectCharge')}
                                disabled={pending}
                                onClick={() => openReason({ kind: 'rejectCharge', chargeId })}
                              >
                                {t('payments.paymentsView.reject')}
                              </Button>
                            </>
                          )}
                          {active?.bucket === 'APPROVALS' &&
                            isManager &&
                            chargeId == null &&
                            paymentId != null && (
                              <>
                                <Button
                                  variant="primary" size="sm"
                                  disabled={pending}
                                  onClick={() =>
                                    approve(key, () => approvePayment.mutateAsync(paymentId))
                                  }
                                >
                                  {t('payments.paymentsView.approve')}
                                </Button>
                                <Button
                                  variant="danger" size="sm"
                                  disabled={pending}
                                  onClick={() => openReason({ kind: 'reject', paymentId })}
                                >
                                  {t('payments.paymentsView.reject')}
                                </Button>
                              </>
                            )}
                          {active?.bucket === 'TO_PAY' && !charge && paymentId != null && (
                            <Button
                              variant="primary" size="sm"
                              disabled={pending}
                              onClick={() => openMarkPaid(paymentId)}
                            >
                              {t('payments.paymentsView.markPaid')}
                            </Button>
                          )}
                          {active?.bucket === 'TO_PAY' &&
                            isManager &&
                            !charge &&
                            paymentId != null && (
                              <Button
                                disabled={pending}
                                variant="danger" size="sm"
                                onClick={() => openReason({ kind: 'voidPayment', paymentId })}
                              >
                                {t('payments.paymentsView.cancelPayout')}
                              </Button>
                            )}
                          {!charge && paymentId != null && (
                            <Button
                              variant="secondary" size="sm"
                              onClick={() => setSelectedPaymentId(paymentId)}
                            >
                              {t('payments.paymentsView.viewDetails')}
                            </Button>
                          )}
                        </div>
                        {actionError?.key === key && (
                          <div role="alert" className={styles.error}>
                            {actionError.message}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <OrderQueueTable
              bucket={bucket}
            rows={visibleRows}
            selected={selection}
            onSelect={(row, checked) =>
              setSelection((old) =>
                checked
                  ? old.length && old[0].fleetManagerUserId !== row.fleetManagerUserId
                    ? old
                    : [...old.filter((item) => item.shipmentId !== row.shipmentId), row]
                  : old.filter((item) => item.shipmentId !== row.shipmentId),
              )
            }
            onPayout={startPayout}
            onInvoice={setInvoiceId}
            onReceipt={(row) => startEntry('IN', row.shipperUserId ?? undefined, row.shipperName)}
            onPayment={setSelectedPaymentId}
            onAllocateCredit={startCredit}
          />
        )}
      </div>
      {reviewCharge?.chargeId != null && (
        <ReviewPartnerChargeFlow
          chargeId={reviewCharge.chargeId}
          isManager={isManager}
          invoiceIssued={Boolean(reviewCharge.issuedInvoiceId)}
          onClose={() => setReviewCharge(null)}
        />
      )}
      {claimId != null && <VerifyClaimFlow paymentId={claimId} onClose={() => setClaimId(null)} />}
      {creditPaymentId != null && (
        <AllocateReceiptCreditFlow
          paymentId={creditPaymentId}
          onAllocated={(fresh, lines) => {
            setCreditPaymentId(null);
            setSelectedPaymentId(fresh.id);
            setCreditSuccess(
              `Receipt #${fresh.id}: ${formatMoney(lines.reduce((sum, line) => sum + parsePaise(line.amount), 0n))} cash allocated for ${lines.map((line) => `#${line.shipmentId}`).join(', ')}. Remaining credit: ${formatMoney(parsePaise(fresh.unallocatedCredit))}.`,
            );
          }}
          onClose={() => setCreditPaymentId(null)}
        />
      )}
      {entryDirection && !counterparty && (
        <CounterpartyPicker
          role={entryDirection === 'IN' ? 'SHIPPER' : 'FLEET_MANAGER'}
          onSelect={setCounterparty}
          onClose={() => setEntryDirection(null)}
        />
      )}
      {entryDirection && counterparty && openItems.isLoading && (
        <p role="status">{t('payments.paymentsView.loadingOpenOrders')}</p>
      )}
      {entryDirection && counterparty && openItems.isError && (
        <div role="alert" className={styles.error}>
          {mapPaymentError(openItems.error)}
          <button onClick={() => void openItems.refetch()}>{t('payments.paymentsView.retryOpenOrders')}</button>
          <button onClick={() => setEntryDirection(null)}>{t('common.actions.cancel')}</button>
        </div>
      )}
      {entryDirection === 'IN' && counterparty && openItems.data && (
        <RecordReceiptDialog
          openItems={openItems.data}
          shipperName={counterparty.fullName ?? `Shipper #${counterparty.id}`}
          onClose={() => {
            setEntryDirection(null);
            setCounterparty(null);
          }}
          submitting={recordReceipt.isPending}
          onSubmit={async (request) => {
            await recordReceipt.mutateAsync({ request });
            setEntryDirection(null);
            setCounterparty(null);
          }}
        />
      )}
      {entryDirection === 'OUT' && counterparty && openItems.data && (
        <NewPayoutDialog
          openItems={openItems.data}
          entryScope={prefillIds.length ? 'SELECTED' : 'PARTNER'}
          initialShipmentIds={prefillIds}
          initialPurpose={payoutPurpose}
          payeeBlockReason={payoutBlockReason}
          onClose={() => {
            setEntryDirection(null);
            setCounterparty(null);
          }}
          submitting={requestPayout.isPending}
          onSubmit={async (request) => {
            const created = await requestPayout.mutateAsync({ request });
            setSelection([]);
            // Demo (#297): the sheet shows who approves it, then the user closes it.
            if (created?.approval) return created;
            setEntryDirection(null);
            setCounterparty(null);
          }}
        />
      )}
      {selectedPaymentId != null && !paymentQuery.data && (
        <div className={styles.drawerScrim} onClick={() => setSelectedPaymentId(null)}>
          <aside
            role="dialog"
            aria-modal="true"
            aria-label={`Payment ${selectedPaymentId}`}
            className={styles.drawer}
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.drawerHeader}>
              <h2>{t('payments.paymentsView.paymentHeading', { id: selectedPaymentId })}</h2>
              <Button variant="secondary" size="sm" onClick={() => setSelectedPaymentId(null)}>
                {t('common.actions.close')}
              </Button>
            </div>
            {paymentQuery.isError ? (
              <div role="alert">
                {detailError(paymentQuery.error)}
                <Button variant="secondary" size="sm" onClick={() => void paymentQuery.refetch()}>
                  {t('payments.paymentsView.retryPaymentDetails')}
                </Button>
              </div>
            ) : (
              <p role="status">{t('payments.paymentsView.loadingPaymentDetails')}</p>
            )}
          </aside>
        </div>
      )}
      {paymentQuery.data && selectedPaymentId && (
        <PaymentDrawer
          payment={paymentQuery.data}
          role={isManager ? 'OPS_MANAGER' : 'OPS_EXECUTIVE'}
          onClose={() => setSelectedPaymentId(null)}
          actionError={
            actionError?.key === `payment-${selectedPaymentId}` ? actionError.message : null
          }
          actionPending={
            approvePayment.isPending ||
            rejectPayment.isPending ||
            markPaymentPaid.isPending ||
            voidPayment.isPending ||
            voidAllocation.isPending
          }
          onVerifyClaim={(id) => {
            setSelectedPaymentId(null);
            setClaimId(id);
          }}
          onApprove={(id) => approve(`payment-${id}`, () => approvePayment.mutateAsync(id))}
          onReject={(id) => openReason({ kind: 'reject', paymentId: id })}
          onMarkPaid={openMarkPaid}
          onAllocateCredit={startCredit}
          onVoidPayment={(id) => openReason({ kind: 'voidPayment', paymentId: id })}
          onVoidAllocation={(allocationId) =>
            openReason({ kind: 'voidAllocation', paymentId: selectedPaymentId, allocationId })
          }
        />
      )}
      {markPaidId && !markPaidQuery.data && (
        <div className={styles.scrim}>
          <section
            role="dialog"
            aria-modal="true"
            aria-label={t('payments.paymentsView.markPayoutPaid')}
            className={`${styles.dialog} ${styles.compact}`}
          >
            <div className={styles.body}>
              {markPaidQuery.isError ? (
                <div role="alert">
                  {mapPaymentError(markPaidQuery.error)}
                  <button onClick={() => void markPaidQuery.refetch()}>{t('payments.paymentsView.retryPayout')}</button>
                </div>
              ) : (
                <p role="status">{t('payments.paymentsView.loadingPayoutSummary')}</p>
              )}
              <Button variant="secondary" size="sm" onClick={() => setMarkPaidId(null)}>
                {t('common.actions.cancel')}
              </Button>
            </div>
          </section>
        </div>
      )}
      {markPaidId && markPaidQuery.data && (
        <MarkPaidDialog
          onReconciledPaid={refreshPayments}
          paymentId={markPaidId}
          payment={markPaidQuery.data}
          payeeName={
            markPaidPayee.data?.payee?.companyName ??
            buckets.flatMap((entry) => entry.rows).find((row) => row.paymentId === markPaidId)
              ?.counterpartyName
          }
          submitting={markPaymentPaid.isPending}
          onClose={() => setMarkPaidId(null)}
          onSubmit={async (paymentId, request) => {
            await markPaymentPaid.mutateAsync({ paymentId, request });
            setMarkPaidId(null);
          }}
        />
      )}
      {invoiceId != null && (
        <GenerateInvoiceDialog
          canEditBuyer
          shipmentId={invoiceId}
          onClose={() => setInvoiceId(null)}
          onIssued={() => {
            setInvoiceId(null);
            void refreshPayments();
          }}
        />
      )}
      {reasonAction?.kind === 'voidAllocation' && (
        <VoidAllocationDialog
          key={`${reasonAction.paymentId}-${reasonAction.allocationId}`}
          paymentId={reasonAction.paymentId}
          allocationId={reasonAction.allocationId}
          submitting={voidAllocation.isPending}
          error={reasonError}
          onCancel={() => setReasonAction(null)}
          onSubmit={submitReason}
        />
      )}
      {reasonAction?.kind === 'voidPayment' && (
        <CancelPaymentDialog
          key={reasonAction.paymentId}
          paymentId={reasonAction.paymentId}
          submitting={voidPayment.isPending}
          error={reasonError}
          onClose={() => setReasonAction(null)}
          onSubmit={submitReason}
        />
      )}
      {reasonAction &&
        reasonAction.kind !== 'voidAllocation' &&
        reasonAction.kind !== 'voidPayment' && (
          <ReasonDialog
            title={reasonAction.kind === 'reject' ? 'Reject payout' : 'Reject charge'}
            actionLabel={reasonAction.kind === 'reject' ? 'Reject payout' : 'Reject charge'}
            help={reasonAction.kind === 'reject' ? 'reject-payout' : 'reject-charge'}
            error={reasonError}
            submitting={
              rejectPayment.isPending ||
              rejectCharge.isPending ||
              voidPayment.isPending ||
              voidAllocation.isPending
            }
            onCancel={() => setReasonAction(null)}
            onSubmit={submitReason}
          />
        )}
    </div>
  );
}
